import { parseFilter } from "@letsdoit/filter-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type { Filter, Item, Label } from "../api/types";
import { useLang, useT } from "../i18n";
import { colorHex, PALETTE } from "../lib/colors";
import { dayHeader } from "../lib/dates";
import { compareOrder, labelCount, sortedLabels } from "../lib/tasks";
import { useApp, useSyncState } from "../store";
import { addFilter, addLabel, deleteFilter, deleteLabel, updateFilter, updateLabel } from "../sync/actions";
import { TaskRow } from "../tasks/TaskRow";
import { AddTask, EmptyState } from "../tasks/TaskTree";
import { IconDrop, IconEdit, IconHeart, IconLabel, IconMore, IconPlus, IconTrash } from "../ui/icons";
import { usePrefs } from "../ui/prefs";
import { Confirm, Menu, Modal, ModalHeader, useAnchor } from "../ui/primitives";
import { applySort, groupTasks, useCompletedTasks } from "./grouping";
import { SectionHeader } from "./sections";
import { useViewOptions, ViewOptionsButton } from "./viewOptions";

/** Split a filter query into its comma-separated top-level groups (Todoist shows one list per group). */
export function splitFilterGroups(query: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote = false;
  let cur = "";
  for (const ch of query) {
    if (ch === '"') quote = !quote;
    if (!quote && (ch === "(" || ch === "（")) depth++;
    if (!quote && (ch === ")" || ch === "）")) depth--;
    if (!quote && depth === 0 && (ch === "," || ch === "，")) {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out.filter(Boolean);
}

type EditTarget = { kind: "filter"; filter?: Filter } | { kind: "label"; label?: Label };

function EditDialog({ target, onClose }: { target: EditTarget; onClose: () => void }) {
  const t = useT();
  const lang = useLang();
  const existing = target.kind === "filter" ? target.filter : target.label;
  const [name, setName] = useState(existing?.name ?? "");
  const [query, setQuery] = useState(target.kind === "filter" ? (target.filter?.query ?? "") : "");
  const [color, setColor] = useState(existing?.color ?? "charcoal");
  const [fav, setFav] = useState(existing?.is_favorite ?? false);
  const check = target.kind === "filter" && query ? parseFilter(query, { lang }) : null;
  const title = existing ? t("edit") : target.kind === "filter" ? t("addFilter") : t("addLabel");
  const submit = () => {
    if (!name.trim()) return;
    if (target.kind === "filter") {
      if (!query.trim() || (check && !check.ok)) return;
      if (target.filter) void updateFilter(target.filter.id, { name: name.trim(), query, color, is_favorite: fav });
      else addFilter(name.trim(), query, color, fav);
    } else if (target.label) void updateLabel(target.label.id, { name: name.trim(), color, is_favorite: fav });
    else addLabel(name.trim(), color, fav);
    onClose();
  };
  return (
    <Modal onClose={onClose} center label={title}>
      <ModalHeader title={title} onClose={onClose} />
      <form
        className="modal-body"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="field">
          <span>{t("name")}</span>
          <input
            className="input"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="fl-name"
          />
        </label>
        {target.kind === "filter" && (
          <label className="field">
            <span>{t("query")}</span>
            <input className="input" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="fl-query" />
            {check && !check.ok && (
              <span className="error-text" data-testid="filter-error">
                {t("filterError", { error: check.error.message })}
              </span>
            )}
          </label>
        )}
        <label className="field">
          <span>{t("color")}</span>
          <select
            className="input"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ color: colorHex(color) }}
          >
            {PALETTE.map((c) => (
              <option key={c.name} value={c.name}>
                ● {lang === "zh" ? c.zh : c.en}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flexDirection: "row", alignItems: "center" }}>
          <input type="checkbox" checked={fav} onChange={(e) => setFav(e.target.checked)} />
          <span style={{ fontWeight: 400 }}>{t("addToFavorites")}</span>
        </label>
        <div className="modal-footer" style={{ margin: "0 -16px -16px" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            data-testid="fl-submit"
            disabled={!name.trim() || (check !== null && !check.ok)}
          >
            {existing ? t("save") : t("add")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RowMenu({
  onEdit,
  onDelete,
  onFav,
  fav,
}: {
  onEdit: () => void;
  onDelete: () => void;
  onFav: () => void;
  fav: boolean;
}) {
  const t = useT();
  const a = useAnchor();
  return (
    <>
      <button
        type="button"
        className="icon-btn"
        aria-label={t("more")}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          a.open(e);
        }}
      >
        <IconMore size={18} />
      </button>
      {a.anchor && (
        <Menu
          anchor={a.anchor}
          onClose={a.close}
          placement="bottom-end"
          items={[
            { key: "edit", label: t("edit"), icon: <IconEdit size={18} />, onSelect: onEdit },
            {
              key: "fav",
              label: fav ? t("removeFromFavorites") : t("addToFavorites"),
              icon: <IconHeart size={18} />,
              onSelect: onFav,
            },
            { key: "sep", label: "", separator: true },
            { key: "delete", label: t("delete"), icon: <IconTrash size={18} />, danger: true, onSelect: onDelete },
          ]}
        />
      )}
    </>
  );
}

export function FiltersLabelsView() {
  const state = useSyncState();
  const t = useT();
  const [edit, setEdit] = useState<EditTarget | null>(null);
  const [confirm, setConfirm] = useState<null | { name: string; run: () => void }>(null);
  const filters = Object.values(state.filters).sort((a, b) => a.item_order - b.item_order || compareOrder(a, b));
  const labels = sortedLabels(state);
  return (
    <div className="view" data-testid="filters-labels-view">
      <div className="view-header">
        <h1 className="view-title">{t("filtersLabels")}</h1>
      </div>
      <div className="section">
        <div className="section-header" style={{ marginLeft: 0 }}>
          <div className="title">{t("myFilters")}</div>
          <button
            type="button"
            className="icon-btn"
            aria-label={t("addFilter")}
            onClick={() => setEdit({ kind: "filter" })}
            data-testid="add-filter"
          >
            <IconPlus size={18} />
          </button>
        </div>
        {filters.map((f) => (
          <Link key={f.id} to={`/app/filter/${f.id}`} className="list-row" data-testid="filter-row">
            <span style={{ color: colorHex(f.color), display: "inline-flex" }}>
              <IconDrop size={20} />
            </span>
            <span className="grow">{f.name}</span>
            <RowMenu
              fav={f.is_favorite}
              onEdit={() => setEdit({ kind: "filter", filter: f })}
              onFav={() => void updateFilter(f.id, { is_favorite: !f.is_favorite })}
              onDelete={() => setConfirm({ name: f.name, run: () => void deleteFilter(f.id) })}
            />
          </Link>
        ))}
      </div>
      <div className="section">
        <div className="section-header" style={{ marginLeft: 0 }}>
          <div className="title">{t("labels")}</div>
          <button
            type="button"
            className="icon-btn"
            aria-label={t("addLabel")}
            onClick={() => setEdit({ kind: "label" })}
            data-testid="add-label"
          >
            <IconPlus size={18} />
          </button>
        </div>
        {labels.map((l) => (
          <Link key={l.id} to={`/app/label/${encodeURIComponent(l.name)}`} className="list-row" data-testid="label-row">
            <span style={{ color: colorHex(l.color), display: "inline-flex" }}>
              <IconLabel size={20} />
            </span>
            <span className="grow">{l.name}</span>
            <span className="muted small">{labelCount(state, l.name) || ""}</span>
            <RowMenu
              fav={l.is_favorite}
              onEdit={() => setEdit({ kind: "label", label: l })}
              onFav={() => void updateLabel(l.id, { is_favorite: !l.is_favorite })}
              onDelete={() => setConfirm({ name: l.name, run: () => void deleteLabel(l.id) })}
            />
          </Link>
        ))}
      </div>
      {edit && <EditDialog target={edit} onClose={() => setEdit(null)} />}
      {confirm && (
        <Confirm
          message={t("deleteConfirm", { name: confirm.name })}
          confirmLabel={t("delete")}
          cancelLabel={t("cancel")}
          onConfirm={confirm.run}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export function LabelView({ name }: { name: string }) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const label = Object.values(state.labels).find((l) => l.name === name);
  const o = useViewOptions("label", label?.id ?? null);
  const tasks = applySort(
    Object.values(state.items)
      .filter((i) => !i.checked && i.labels.includes(name) && state.projects[i.project_id])
      .sort(compareOrder),
    o,
    prefs.tz,
  );
  const groups = groupTasks(tasks, o.groupedBy, { state, tz: prefs.tz, today: prefs.today, lang: prefs.lang });
  return (
    <div className="view" data-testid="label-view">
      <div className="view-header">
        <h1 className="view-title">
          <span style={{ color: colorHex(label?.color) }}>@</span>
          {name}
        </h1>
        {label && (
          <ViewOptionsButton
            viewType="label"
            objectId={label.id}
            layouts={["list"]}
            groupings={["none", "due_date", "priority", "project"]}
          />
        )}
      </div>
      <GroupedTasks groups={groups} tasks={tasks} />
      <AddTask defaults={{ labels: [name] }} />
      {!tasks.length && <EmptyState text={t("noTasks")} />}
    </div>
  );
}

function GroupedTasks({ groups, tasks }: { groups: ReturnType<typeof groupTasks>; tasks: Item[] }) {
  if (!groups) return tasks.map((it) => <TaskRow key={it.id} item={it} showProject />);
  return groups.map((g) => (
    <div className="section" key={g.key}>
      <SectionHeader title={g.title} count={g.tasks.length} />
      {g.tasks.map((it) => (
        <TaskRow key={it.id} item={it} showProject />
      ))}
    </div>
  ));
}

interface FilterResultsState {
  loading: boolean;
  error: string | null;
  groups: { query: string; ids: string[]; fetched: Item[] }[];
}

/** Run a saved filter on the server (`GET /tasks/filter`), one request per comma group. */
export function useFilterResults(query: string | undefined): FilterResultsState {
  const client = useApp((s) => s.client);
  const _syncToken = useSyncState().syncToken;
  const lang = useLang();
  const [res, setRes] = useState<FilterResultsState>({ loading: true, error: null, groups: [] });
  useEffect(() => {
    if (!query) return;
    const local = parseFilter(query, { lang });
    if (!local.ok) {
      setRes({ loading: false, error: local.error.message, groups: [] });
      return;
    }
    let alive = true;
    const parts = splitFilterGroups(query);
    Promise.all(
      parts.map(async (q) => {
        const all: Item[] = [];
        let cursor: string | undefined;
        for (let page = 0; page < 10; page++) {
          const r = await client.get<{ results: Item[]; next_cursor: string | null }>("/tasks/filter", {
            query: q,
            lang,
            limit: "200",
            cursor,
          });
          all.push(...r.results);
          if (!r.next_cursor) break;
          cursor = r.next_cursor;
        }
        return { query: q, ids: all.map((i) => i.id), fetched: all };
      }),
    )
      .then((groups) => alive && setRes({ loading: false, error: null, groups }))
      .catch((e: Error) => alive && setRes({ loading: false, error: e.message, groups: [] }));
    return () => {
      alive = false;
    };
  }, [query, client, lang]);
  return res;
}

export function FilterView({ filterId }: { filterId: string }) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const filter = state.filters[filterId];
  const o = useViewOptions("filter", filterId);
  const res = useFilterResults(filter?.query);
  const [edit, setEdit] = useState(false);
  const groups = useMemo(
    () =>
      res.groups.map((g) => ({
        query: g.query,
        // Prefer the local copy (optimistic edits); drop tasks completed/deleted locally.
        tasks: applySort(
          g.fetched.flatMap((f) => {
            const local = state.items[f.id];
            return local && !local.checked ? [local] : [];
          }),
          o,
          prefs.tz,
        ),
      })),
    [res.groups, state, o, prefs.tz],
  );
  if (!filter) return <EmptyState text={t("noTasks")} />;
  return (
    <div className="view" data-testid="filter-view">
      <div className="view-header">
        <div>
          <h1 className="view-title">{filter.name}</h1>
          <div className="view-subtitle">{filter.query}</div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <ViewOptionsButton
            viewType="filter"
            objectId={filterId}
            layouts={["list"]}
            groupings={["none", "due_date", "priority", "label", "project"]}
          />
          <button type="button" className="icon-btn" onClick={() => setEdit(true)} aria-label={t("edit")}>
            <IconEdit size={18} />
          </button>
        </div>
      </div>
      {res.error && (
        <div className="error-text" data-testid="filter-error">
          {t("filterError", { error: res.error })}
        </div>
      )}
      {groups.map((g) => {
        const sub = groupTasks(g.tasks, o.groupedBy, { state, tz: prefs.tz, today: prefs.today, lang: prefs.lang });
        return (
          <div className="section" key={g.query}>
            {groups.length > 1 && <SectionHeader title={g.query} count={g.tasks.length} />}
            <GroupedTasks groups={sub} tasks={g.tasks} />
          </div>
        );
      })}
      {!res.loading && !res.error && groups.every((g) => g.tasks.length === 0) && <EmptyState text={t("noTasks")} />}
      {edit && <EditDialog target={{ kind: "filter", filter }} onClose={() => setEdit(false)} />}
    </div>
  );
}

export function CompletedView() {
  const prefs = usePrefs();
  const t = useT();
  const [days, setDays] = useState(14);
  const items = useCompletedTasks({}, days);
  const byDay = new Map<string, Item[]>();
  for (const it of [...items].sort((a, b) => ((b.completed_at ?? "") < (a.completed_at ?? "") ? -1 : 1))) {
    const d = it.completed_at ? new Date(it.completed_at).toLocaleDateString("en-CA", { timeZone: prefs.tz }) : "?";
    const list = byDay.get(d) ?? [];
    list.push(it);
    byDay.set(d, list);
  }
  return (
    <div className="view" data-testid="completed-view">
      <div className="view-header">
        <h1 className="view-title">{t("completed")}</h1>
      </div>
      {[...byDay.entries()].map(([d, list]) => (
        <div className="section" key={d}>
          <SectionHeader title={dayHeader(d, prefs.today, prefs.lang)} count={list.length} />
          {list.map((it) => (
            <TaskRow key={it.id} item={{ ...it, checked: true }} showProject />
          ))}
        </div>
      ))}
      {!items.length && <EmptyState text={t("noCompleted")} />}
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <button type="button" className="btn btn-secondary" onClick={() => setDays(days + 30)}>
          {t("loadMore")}
        </button>
      </div>
    </div>
  );
}
